import React from "react";
import { CreateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, getObjectValues, StyleProps } from "@hex-insights/core";
import { Form, FormState, SubmitButton, useInternalFields } from "@hex-insights/forms";
import { StudentNoteCreateMutation } from "../../../../../Utilities";
import { StudentNoteFormat } from "../../../../Format";
import { StudentNoteFormState, StudentNoteFormValues, StudentNoteTypeFormValues } from "../../../../FormUtilities";
import { BaseFormProps } from "../../../shared";
import * as Field from "../../Field";
import { useCreateFormControl, useCreateFormSubmission } from "../../hooks";

const resolutionFields: (keyof StudentNoteFormValues.Base)[] = ["isResolved", "resolvedAt", "resolutionDescription"];

export type ControlledCreateProps = Omit<
	CreateProps<StudentNoteCreateMutation["createStudentNote"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentNoteFormState.UseCreateFormStateArgs;
};

export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl({
		...formStateArgs,
		initialFormValues: { ...StudentNoteTypeFormValues.initialMedicationCreate, ...formStateArgs?.initialFormValues },
	});

	return <Create formState={formState} create={create} {...props} />;
}

const hiddenFields: (keyof StudentNoteFormValues.Create)[] = [
	"noteType",
	"secondaryStudentIDs",
	"studentAcademicNote",
	"studentAttendanceNote",
	"studentBehaviorNote",
	"studentHealthNote",
	"studentIncidentNote",
	"studentInjuryNote",
	"studentSocialEmotionalNote",
];

export type CreateProps<T> = {
	formState: FormState<StudentNoteFormValues.Create>;
	create: CreateFunction<StudentNoteFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentNoteFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<StudentNoteFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	columnClassName,
	columnStyle,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentNote.create.medication" + (formNameSuffix ? "." + formNameSuffix : "");

	useInternalFields(formState, hiddenFields);
	useInternalFields(formState, resolutionFields);

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column className={columnClassName} style={columnStyle}>
				<Field.PrimaryStudents.Select formState={formState} label="Students" style={{ width: "100%" }} />
				<Field.Body formState={formState} label={StudentNoteFormat.bodyLabels.medication} style={{ width: "100%" }} />
				<Field.StudentMedicationNote.SubForm
					formState={formState}
					noHeader
					style={
						{
							width: "100%",
							"--sub-form-field__value---padding": 0,
							"--sub-form-field__value---border-width": 0,
						} as React.CSSProperties
					}
				/>
				<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />

				<Field.SharingRecord.SubForm formState={formState} />

				<Field.IsPinned formState={formState} style={{ width: "100%" }} />
				<Field.RequiresResolution formState={formState} style={{ width: "100%" }} />

				<Form.ActionBar>
					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
					>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}
